import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { venueListSubscriptionsApi } from '../../../api/stripe/venueListSubscriptionsApi';
import { extractSubscriptionPlan } from '../../../pages/SubscriptionPlans/common/helper';
import { BillingCycle, Plan } from '../../../pages/SubscriptionPlans/types';
import { useAppSelector } from '../../../redux/hooks';
import { VENUE_MANUAL_TAGS } from '../../../utils/venue-tags';

export type MigrationRenewalState = {
  needsMigrationRenewal: boolean;
  lastSku?: string;
  plan?: Plan;
  cycle?: BillingCycle;
  isBrand: boolean;
};

// Subscriptions created before this date were on Vantage Pass and lost their
// auto-renewal at the Vantage Pass → Nomad Sync Ltd. Stripe migration.
// Subs created on/after this date are already on NOMAD and renew normally.
// Confirmed by backend (Imad): migration happened on 2026-05-01.
const MIGRATION_DATE = new Date('2026-05-01T00:00:00Z');

// localStorage key prefix used to suppress the modal for a venue that explicitly
// chose to stay on the Free plan after migration. Brands aren't given this
// option (their Free plan is essentially locked-out), so this only applies to
// venues. Stored per venueId so a user managing multiple venues can opt out
// granularly.
export const STAY_ON_FREE_STORAGE_PREFIX = 'migration-renewal-stay-free-';

// --- Dev-only test override -------------------------------------------------
// Lets you trigger the modal from the URL while developing, e.g.
//   /?migrationRenewal=1&lastSku=starter_brand_1_
// The override is persisted in sessionStorage so it survives navigations.
// Setting `?migrationRenewal=0` clears the override.
// Disabled in production builds.
const TEST_STORAGE_KEY = 'migration-renewal-test-override';
const SHOWN_STORAGE_KEY = 'migration-renewal-shown';

type TestOverride = { enabled: boolean; lastSku?: string };

const readTestOverride = (): TestOverride | null => {
  if (typeof window === 'undefined') return null;
  if (process.env.NODE_ENV === 'production') return null;

  const params = new URLSearchParams(window.location.search);
  const urlFlag = params.get('migrationRenewal');

  if (urlFlag === '0') {
    sessionStorage.removeItem(TEST_STORAGE_KEY);
    sessionStorage.removeItem(SHOWN_STORAGE_KEY);
    return null;
  }

  if (urlFlag === '1') {
    const data: TestOverride = {
      enabled: true,
      lastSku: params.get('lastSku') ?? undefined,
    };
    sessionStorage.setItem(TEST_STORAGE_KEY, JSON.stringify(data));
    sessionStorage.removeItem(SHOWN_STORAGE_KEY);
    return data;
  }

  const stored = sessionStorage.getItem(TEST_STORAGE_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored) as TestOverride;
  } catch {
    return null;
  }
};
// --- end dev-only override --------------------------------------------------

// Standard SKU shape: <plan>_<business>_<cycle>(_v3)? with optional trailing _.
// When the backend returns a non-standard sku (e.g. 'free' for users dropped
// to the free tier on expiration), we fall back to reconstructing the sku
// from productGroup + price.
const STANDARD_SKU_PATTERN = /^(basic|starter|plus)_(venue|brand)_\d+(_final)?(_v\d+)?_?$/;

export const useMigrationRenewal = (): MigrationRenewalState => {
  const venue = useAppSelector((state) => state.venue.value);
  const {
    subscription,
    pricing,
    isLoading: subscriptionLoading,
  } = useAppSelector((s) => s.subscription.value);
  const venueID = venue?.id;
  // The venue Free plan is also `status: 'active'` (€0), so plain status isn't
  // enough to tell who's actually paying. We require a positive price so that
  // venues dropped to Free still go through the migration renewal flow.
  const hasActivePaidSubscription =
    subscription?.status === 'active' && Number(subscription?.price ?? 0) > 0;

  // Only fetch historical subs when there's no truly active paid one. Users
  // who are currently paying don't need the renewal modal.
  const shouldFetchHistorical =
    !subscriptionLoading && !hasActivePaidSubscription && Boolean(venueID);

  const { data: historicalSubs } = useQuery({
    queryKey: ['migration-renewal-subscriptions', venueID],
    queryFn: () => venueListSubscriptionsApi({ venueID: Number(venueID) }),
    enabled: shouldFetchHistorical,
  });

  return useMemo(() => {
    const isBrand = Boolean(venue?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans));

    // Dev-only override takes precedence so we can demo / debug freely.
    const override = readTestOverride();
    if (override?.enabled && override.lastSku) {
      const [plan, , cycle] = extractSubscriptionPlan(override.lastSku);
      return {
        needsMigrationRenewal: true,
        lastSku: override.lastSku,
        plan,
        cycle,
        isBrand,
      };
    }

    // Active paid subscription → user is paying, no modal.
    if (hasActivePaidSubscription) {
      return { needsMigrationRenewal: false, isBrand };
    }

    if (!historicalSubs || historicalSubs.length === 0) {
      return { needsMigrationRenewal: false, isBrand };
    }

    // Only consider previously paid subscriptions: skip Free / €0 records,
    // otherwise venues dropped to Free (which can still appear in this list
    // with an expirationDate) would be picked over the real expired paid sub.
    const paidHistoricalSubs = historicalSubs.filter(
      (s) => s != null && Number(s.price ?? 0) > 0,
    );

    if (paidHistoricalSubs.length === 0) {
      return { needsMigrationRenewal: false, isBrand };
    }

    // Pick the subscription with the latest expirationDate.
    const latest = paidHistoricalSubs.reduce<(typeof paidHistoricalSubs)[number] | undefined>(
      (best, current) => {
        if (!current?.expirationDate) return best;
        if (!best) return current;
        return new Date(current.expirationDate) > new Date(best.expirationDate ?? 0)
          ? current
          : best;
      },
      undefined,
    );

    if (!latest?.expirationDate || !latest.sku) {
      return { needsMigrationRenewal: false, isBrand };
    }

    // The migration only affects subs that existed on the old (Vantage Pass)
    // Stripe account. Subs created on/after the migration date are already on
    // NOMAD and follow the normal renewal flow — they don't need this modal.
    const createdBeforeMigration =
      latest.createdAt != null && new Date(latest.createdAt) < MIGRATION_DATE;
    // We also need the sub to have expired AFTER the migration: a VP sub that
    // expired long before the migration means the venue has been on Free for a
    // while and isn't part of the cohort affected by the renewal cancellation.
    const expiredAfterMigration = new Date(latest.expirationDate) > MIGRATION_DATE;
    if (!createdBeforeMigration || !expiredAfterMigration) {
      return { needsMigrationRenewal: false, isBrand };
    }

    // If the sku is in the expected shape, use it directly. Otherwise (e.g.
    // backend wrote 'free' on expiration), reconstruct it by matching
    // productGroup + price against the available Stripe prices: this recovers
    // both the cycle and the v3/legacy tier from semantic data.
    let effectiveSku = latest.sku;
    if (!STANDARD_SKU_PATTERN.test(effectiveSku) && latest.productGroup && latest.price != null) {
      const expectedCurrency = latest.currency?.toLowerCase();
      const matched = pricing?.find((p) => {
        const productSku = p?.metadata?.ProductSku;
        if (!productSku) return false;
        if (!productSku.startsWith(`${latest.productGroup}_`)) return false;
        if (expectedCurrency && p.currency !== expectedCurrency) return false;
        return p.unit_amount === Math.round(Number(latest.price) * 100);
      });
      if (matched?.metadata?.ProductSku) {
        effectiveSku = matched.metadata.ProductSku;
      }
    }

    if (!STANDARD_SKU_PATTERN.test(effectiveSku)) {
      return { needsMigrationRenewal: false, isBrand };
    }

    const [plan, , cycle] = extractSubscriptionPlan(effectiveSku);

    return {
      needsMigrationRenewal: true,
      lastSku: effectiveSku,
      plan,
      cycle,
      isBrand,
    };
  }, [venue?.tags, hasActivePaidSubscription, historicalSubs, pricing]);
};
